import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Accordion from '../../../shared/Accordion';
import ConfirmDialog from '../../../shared/ConfirmDialog';
import CancelButton from '../../../shared/CancelButton';
import DateInput from '../../../shared/DateInput';
import NumberInput from '../../../shared/NumberInput';
import RequiredMark from '../../../shared/RequiredMark';
import { CategoryPill, Pill } from '../../../shared/Pill';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { todayInTenant, formatDate } from '../../../shared/tenantClock';
import { formatPrice } from '../Prices/priceLookupShared';
import '../../../shared/StyledInput.css';
import { askConfirm } from '../../../shared/AppDialogs';
import { ReceiptQuickbooksTag, receiptVoidedLabel, voidReceiptQuestion, markInQuickbooksQuestion } from '../../../shared/receiptShared';

// Payments a Customer makes against their Quote or Sales Order (issue
// #338): a Prepay Customer's, or any other's once staff confirm (a deposit,
// an upfront payment). Staff record each one here. A Quote's payments carry
// across to the Sales Order it's converted to. Each is a Receipt with its
// own number (issue #360); a Receipt is never deleted, only voided, and a
// voided one stays listed. Once the Invoice Cutover allows it (issue #454) a
// Receipt is copied to QuickBooks as it's recorded, and voided there when it's
// voided (issue #342); one that didn't get there shows Not in QuickBooks
// with the reason and a Retry, and staff can mark one as already in
// QuickBooks so it's never copied. A Sales Order dated before the Invoice
// Cutover says its Invoice and payment are in QuickBooks, and any Receipts it
// already has are read-only.

const LINK_BUTTON = { background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, padding: 0 };

const PAYMENT_STATUS = {
  unpaid: { label: 'Unpaid', color: '#b91c1c' },
  part_paid: { label: 'Part-paid', color: '#b45309' },
  paid: { label: 'Paid', color: '#1a7f4b' },
  no_charge: { label: 'No charge', color: '#5a6b85' },
};

// Unpaid / Part-paid / Paid / No charge — on the document's header and both lists.
// Nothing for a Customer who isn't on a Prepay Payment Term and has no
// payments recorded on the document (status null).
export function PaymentStatusPill({ status, title }) {
  const s = PAYMENT_STATUS[status];
  if (!s) return null;
  return <CategoryPill value={s.label} color={s.color} title={title} />;
}

// The document's payments, fresh from the server — for a check made right
// before a step, such as the Sales Order's Accept warning.
// 'Balance owing $X', or 'Overpaid $X' once more than the Total is in.
export function balanceText(balance) {
  return balance < 0 ? `Overpaid ${formatPrice(-balance)}` : `Balance owing ${formatPrice(balance)}`;
}

export async function fetchPayments(basePath, documentId) {
  const res = await axios.get(`/api/${basePath}/${documentId}/payments`);
  return res.data.data;
}

const LABEL_STYLE = { display: 'block', fontSize: 13, marginBottom: 12 };
const CELL = { padding: '6px 8px', fontSize: 14 };
const HEAD = { padding: '6px 8px', fontSize: 12, fontWeight: 600, color: '#5a6b85', textAlign: 'left', borderBottom: '1px solid #b3bbc7' };

// basePath: 'quotes' or 'sales-orders'. recordOpen/onRecordOpenChange: the
// Record Payment dialog, opened from the page's Actions menu. onSummary
// gets the latest figures (or null before they load) so the page can show
// the status pill and offer Record Payment. onChanged runs after a payment
// is recorded or voided (the document's status or Status History may have
// moved). `reloadKey` refetches when the document's Total may have changed.
// What's still owing, for the Record Payment dialog: the server leaves
// balance_owing null until a document's payments are tracked (Prepay, or a
// first payment), but the first payment against it still owes the Total.
function owingOf(data) {
  if (data.balance_owing !== null && data.balance_owing !== undefined) return data.balance_owing;
  return Math.round((Number(data.total) - (Number(data.total_paid) || 0)) * 100) / 100;
}

function DocumentPayments({ basePath, documentId, recordOpen, onRecordOpenChange, onSummary, onChanged, canUpdate, reloadKey }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const apply = (next) => {
    setData(next);
    if (onSummary) onSummary(next);
  };

  useEffect(() => {
    let cancelled = false;
    if (!documentId) return undefined;
    fetchPayments(basePath, documentId)
      .then((next) => { if (!cancelled) apply(next); })
      .catch(() => { if (!cancelled) apply(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath, documentId, reloadKey]);

  // A fresh form each time the dialog opens: today, the balance owing, and
  // the document number the printed bank details ask the Customer to quote.
  useEffect(() => {
    if (recordOpen && data) {
      setForm({
        payment_date: todayInTenant(),
        amount: owingOf(data) > 0 ? String(owingOf(data)) : '',
        reference: data.default_reference || '',
        paid_into_account_id: data.default_paid_into_account_id ? String(data.default_paid_into_account_id) : '',
        already_in_quickbooks: false,
      });
    }
    if (!recordOpen) setForm(null);
  }, [recordOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each soft check the server raises (NOT_PREPAY, OVERPAYMENT) is
  // confirmed here, then the payment is sent again with it confirmed.
  const CONFIRMS = { NOT_PREPAY: 'confirm_not_prepay', OVERPAYMENT: 'confirm_overpay' };
  const save = async (confirmed = {}) => {
    setSaving(true);
    try {
      const res = await axios.post(`/api/${basePath}/${documentId}/payments`, {
        payment_date: form.payment_date,
        amount: form.amount,
        reference: form.reference,
        paid_into_account_id: form.paid_into_account_id,
        already_in_quickbooks: data.copies_to_quickbooks && form.already_in_quickbooks,
        ...confirmed,
      });
      apply(res.data.data);
      onRecordOpenChange(false);
      dismissError();
      if (onChanged) onChanged();
    } catch (err) {
      const confirmKey = CONFIRMS[err.response?.data?.error];
      if (confirmKey && !confirmed[confirmKey]) {
        setSaving(false);
        if (await askConfirm(`${err.response.data.message} Record it anyway?`, { confirmLabel: 'Record Anyway' })) save({ ...confirmed, [confirmKey]: true });
        return;
      }
      showError(err, 'Failed to record payment');
    }
    setSaving(false);
  };

  const handleVoid = async (payment) => {
    if (!(await askConfirm(voidReceiptQuestion(payment), { confirmLabel: 'Void', destructive: true }))) return;
    try {
      const res = await axios.post(`/api/${basePath}/${documentId}/payments/${payment.id}/void`);
      apply(res.data.data);
      dismissError();
      if (onChanged) onChanged();
    } catch (err) {
      showError(err, 'Failed to void Receipt');
    }
  };

  // Retry, or Mark as already in QuickBooks: the response shows where it
  // landed.
  const quickbooksStep = async (payment, step, failure) => {
    setBusyId(payment.id);
    try {
      const res = await axios.post(`/api/${basePath}/${documentId}/payments/${payment.id}/${step}`);
      apply(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, failure);
    }
    setBusyId(null);
  };
  const handleMark = async (payment) => {
    if (!(await askConfirm(markInQuickbooksQuestion(payment), { confirmLabel: 'Mark as in QuickBooks' }))) return;
    quickbooksStep(payment, 'mark-in-quickbooks', 'Failed to mark Receipt');
  };

  if (!data) return null;
  // The box is for a Prepay Customer, or once any payment is recorded; the
  // Record Payment dialog opens either way.
  const showBox = data.is_prepay || data.has_receipts;
  const canVoid = canUpdate && data.can_void;
  const amountValid = Number(form?.amount) > 0;
  const totalRows = [
    { label: 'Total', value: data.total },
    { label: 'Total paid', value: data.total_paid },
    { label: data.balance_owing < 0 ? 'Overpaid' : 'Balance owing', value: Math.abs(data.balance_owing), strong: true },
  ];

  return (
    <div>
      {data.before_invoice_cutover && (
        <div style={{ fontSize: 13, color: '#5a6b85', background: '#eef1f5', border: '1px solid #d5dbe3', borderRadius: 6, padding: '8px 12px', marginBottom: 16 }}>
          This Sales Order is dated before the Invoice Cutover, so its Invoice and payment are in QuickBooks.
        </div>
      )}
      {showBox && (
        <Accordion
          title="Payments"
          showMenuPlaceholder={false}
          headerExtra={(
            <span style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14 }}>
              <PaymentStatusPill status={data.payment_status} />
              <span style={{ fontWeight: 700 }}>{balanceText(data.balance_owing)}</span>
            </span>
          )}
        >
          {data.payments.length ? (
            <table style={{ width: '100%', maxWidth: 900, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={HEAD}>Receipt #</th>
                  <th style={HEAD}>Date Paid</th>
                  <th style={HEAD}>Payment Reference</th>
                  <th style={HEAD}>Paid into</th>
                  <th style={HEAD}>Recorded by</th>
                  <th style={{ ...HEAD, textAlign: 'right' }}>Amount</th>
                  <th style={{ ...HEAD, width: 48 }} />
                </tr>
              </thead>
              <tbody>
                {data.payments.map((p) => (
                  <React.Fragment key={p.id}>
                    <tr style={{ color: p.voided_at ? '#8a94a6' : undefined }}>
                      <td style={{ ...CELL, fontWeight: 600 }}>
                        {p.display_number}
                        {p.voided_at && <div><Pill color="#5a6b85" background="#eef1f5" style={{ fontSize: 11, padding: '1px 8px' }}>Void</Pill></div>}
                        <ReceiptQuickbooksTag receipt={p} />
                      </td>
                      <td style={{ ...CELL, fontSize: 13, color: p.voided_at ? undefined : '#666' }}>{formatDate(p.payment_date)}</td>
                      <td style={CELL}>{p.reference}</td>
                      <td style={{ ...CELL, fontSize: 13 }}>{p.paid_into_label || ''}</td>
                      <td style={{ ...CELL, fontSize: 13, color: p.voided_at ? undefined : '#666' }}>{p.recorded_by_name || '—'}</td>
                      <td style={{ ...CELL, textAlign: 'right', textDecoration: p.voided_at ? 'line-through' : 'none' }}>{formatPrice(p.amount)}</td>
                      <td style={{ ...CELL, textAlign: 'center' }}>
                        {canVoid && !p.voided_at && !p.read_only && (
                          <RowActionsMenu ariaLabel={`Actions for Receipt ${p.display_number}`} items={[{ key: 'void', label: 'Void Receipt', danger: true, needs: 'update', onClick: () => handleVoid(p) }]} />
                        )}
                      </td>
                    </tr>
                    {p.voided_at && (
                      <tr>
                        <td colSpan={7} style={{ padding: '0 8px 6px', fontSize: 12, color: '#8a94a6' }}>{receiptVoidedLabel(p)}</td>
                      </tr>
                    )}
                    {p.quickbooks?.state === 'failed' && (
                      <tr>
                        <td colSpan={7} style={{ padding: '0 8px 6px', fontSize: 12, color: '#b91c1c' }}>
                          <span style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                            <span>{p.quickbooks.message}</span>
                            {canUpdate && (
                              <button type="button" onClick={() => quickbooksStep(p, 'quickbooks-retry', 'Retry failed')} disabled={busyId === p.id} title="Sends this Receipt to QuickBooks again" style={LINK_BUTTON}>
                                {busyId === p.id ? 'Sending...' : 'Retry'}
                              </button>
                            )}
                            {canUpdate && !p.voided_at && (
                              <button type="button" onClick={() => handleMark(p)} disabled={busyId === p.id} title="Marks this Receipt as already in QuickBooks, so it's never copied" style={LINK_BUTTON}>
                                Mark as already in QuickBooks
                              </button>
                            )}
                          </span>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              {/* Totals sit in the table's own Amount column so the figures line up. */}
              <tfoot>
                {totalRows.map(({ label, value, strong }, i) => (
                  <tr key={label} style={strong ? { fontWeight: 700 } : undefined}>
                    <td colSpan={5} style={{ ...CELL, textAlign: 'right', paddingTop: i === 0 ? 14 : strong ? 8 : 3, paddingBottom: 3 }}>{label}</td>
                    <td style={{ ...CELL, textAlign: 'right', paddingTop: i === 0 ? 14 : strong ? 8 : 3, paddingBottom: 3 }}>{formatPrice(value)}</td>
                    <td />
                  </tr>
                ))}
              </tfoot>
            </table>
          ) : (
            <>
              <div style={{ fontSize: 14, color: '#888' }}>No payments recorded yet.</div>
              <div style={{ maxWidth: 900, marginTop: 8 }}>
                {totalRows.map(({ label, value, strong }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, fontSize: 14, fontWeight: strong ? 700 : undefined, padding: `${strong ? 6 : 3}px 56px 3px 8px` }}>
                    <span>{label}</span>
                    <span style={{ minWidth: 110, textAlign: 'right' }}>{formatPrice(value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Accordion>
      )}

      {recordOpen && form && (
        <ConfirmDialog
          open
          onOpenChange={(next) => { if (!next && !saving) onRecordOpenChange(false); }}
          title="Record Payment"
          description={`${balanceText(owingOf(data))} of ${formatPrice(data.total)}. ${data.copies_to_quickbooks ? 'It’s copied to QuickBooks as soon as it’s recorded.' : 'Record the payment in QuickBooks too.'}`}
          footer={(
            <>
              <CancelButton onClick={() => onRecordOpenChange(false)} disabled={saving} />
              <button
                type="button"
                onClick={() => save()}
                disabled={saving || !form.payment_date || !amountValid || !form.reference.trim() || !form.paid_into_account_id}
                style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              >
                {saving ? 'Recording...' : 'Record Payment'}
              </button>
            </>
          )}
        >
          <label style={LABEL_STYLE}>
            Date Paid
            <DateInput
              className="styled-input"
              value={form.payment_date}
              max={todayInTenant()}
              onChange={(e) => setForm((prev) => ({ ...prev, payment_date: e.target.value }))}
              style={{ display: 'block', width: 150, marginTop: 4, fontSize: 13 }}
            />
          </label>
          <label style={LABEL_STYLE}>
            Amount
            <NumberInput
              className="styled-input"
              value={form.amount}
              decimals={2}
              min={0}
              onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
              style={{ display: 'block', width: 150, marginTop: 4, fontSize: 13 }}
            />
          </label>
          <label style={LABEL_STYLE}>
            Payment Reference
            <input
              className="styled-input"
              value={form.reference}
              maxLength={100}
              onChange={(e) => setForm((prev) => ({ ...prev, reference: e.target.value }))}
              style={{ display: 'block', width: '100%', marginTop: 4, fontSize: 13, boxSizing: 'border-box' }}
            />
          </label>
          <label style={{ ...LABEL_STYLE, marginBottom: 0 }}>
            Paid into<RequiredMark />
            <select
              className="styled-input"
              value={form.paid_into_account_id}
              onChange={(e) => setForm((prev) => ({ ...prev, paid_into_account_id: e.target.value }))}
              style={{ display: 'block', width: '100%', marginTop: 4, fontSize: 13, boxSizing: 'border-box' }}
            >
              <option value="">Select bank Account</option>
              {data.paid_into_options.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
            {!data.paid_into_options.length && (
              <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>No active bank Accounts. Add one under Accounting › Accounts.</div>
            )}
          </label>
          {data.copies_to_quickbooks && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 12 }}>
              <input
                type="checkbox"
                checked={form.already_in_quickbooks}
                onChange={(e) => setForm((prev) => ({ ...prev, already_in_quickbooks: e.target.checked }))}
              />
              Already in QuickBooks (keyed in by hand, so don't copy it)
            </label>
          )}
        </ConfirmDialog>
      )}
    </div>
  );
}

export default DocumentPayments;
